import { useState } from 'react';
import { Bell, ChevronDown, ChevronRight } from 'lucide-react';
import { clsx } from 'clsx';
import { LIEFERWUNSCH_LABEL, LIEFERWUNSCH_VORGABE } from '@/lib/lieferwunsch';
import type { Lieferwunsch } from '@/types/detail';
import { FOKUS_RING } from '@/lib/fokus';

/**
 * Der Kundenwunsch bei der Erfassung (A56, #135).
 *
 * ZWEI ANGABEN, DIE AUF JEDER KARTEIKARTE STANDEN
 *   Wann geliefert werden soll — erst wenn alles da ist, oder sobald
 *   etwas da ist — und ob eine Nachricht gewünscht ist.
 *
 * ZURÜCKHALTEND, ABER NICHT VERSTECKT
 *   Der Betreiber: „etwas versteckt, es kommt nicht oft vor und soll
 *   das Formular nicht aufblähen". Deshalb zugeklappt als Vorgabe.
 *   ABER: Ist etwas vom Voreingestellten abweichend gesetzt, steht das
 *   im ZUGEKLAPPTEN Zustand daneben. Ein Bereich, der verschweigt, dass
 *   in ihm etwas steht, ist eine Falle — jemand klappt ihn nie auf und
 *   erfährt nie, dass die Kundin Teillieferung wollte.
 *
 * DER WUNSCH JE POSITION STEHT NICHT HIER
 *   Er gilt EINEM Titel; hier müsste man ihn einer Position zuordnen,
 *   und dafür bräuchte es eine Liste neben der Liste. Er steht als
 *   Schalter in der Reihe der kleinen Handgriffe an der Position selbst
 *   (PositionCard).
 *
 * DAS SYSTEM VERSCHICKT NICHTS (Designprinzip 3). Hier wird ein Wunsch
 * festgehalten, mehr nicht — kein Versandweg, keine Mail, keine SMS.
 */
export function KundenwunschBereich({
  lieferwunsch,
  nachrichtWunsch,
  onLieferwunsch,
  onNachrichtWunsch
}: {
  lieferwunsch: Lieferwunsch;
  nachrichtWunsch: boolean;
  onLieferwunsch: (wunsch: Lieferwunsch) => void;
  onNachrichtWunsch: () => void;
}) {
  const [offen, setOffen] = useState(false);

  // Was im zugeklappten Zustand zu sehen sein MUSS. Nur Abweichendes:
  // „Erst wenn alles da ist" ist die Vorgabe und steht da, ohne dass es
  // jemand gesagt hat — es hier zu nennen machte aus jeder Bestellung
  // eine mit Sonderwunsch.
  const abweichend: string[] = [];
  if (lieferwunsch !== LIEFERWUNSCH_VORGABE) abweichend.push(LIEFERWUNSCH_LABEL[lieferwunsch]);
  if (nachrichtWunsch) abweichend.push('Nachricht gewünscht');

  return (
    <section data-tour="sheet-kundenwunsch">
      <button
        type="button"
        onClick={() => setOffen((o) => !o)}
        aria-expanded={offen}
        className={`flex min-h-[44px] w-full items-center gap-2 text-left ${FOKUS_RING}`}
      >
        {offen ? (
          <ChevronDown aria-hidden className="h-4 w-4 shrink-0 text-ink-faint" />
        ) : (
          <ChevronRight aria-hidden className="h-4 w-4 shrink-0 text-ink-faint" />
        )}
        <span className="text-sm font-medium text-ink">Kundenwunsch</span>
        {/* Die Marke steht NEBEN dem Titel mit `shrink-0`, nicht in ihm:
            sonst verschwände sie mit dem Text (die Lehre aus A43/A45). */}
        {!offen && abweichend.length > 0 && (
          <span className="shrink-0 rounded bg-akzent-sanft px-1.5 py-0.5 text-xs text-akzent-stark">
            {abweichend.join(' · ')}
          </span>
        )}
      </button>

      {offen && (
        <div className="mt-2 space-y-3 rounded-md border border-line bg-cream-warm p-3">
          <fieldset>
            <legend className="text-xs text-ink-soft">Liefern</legend>
            <div className="mt-1 flex flex-wrap gap-2">
              {(Object.keys(LIEFERWUNSCH_LABEL) as Lieferwunsch[]).map((w) => (
                <button
                  key={w}
                  type="button"
                  onClick={() => onLieferwunsch(w)}
                  aria-pressed={lieferwunsch === w}
                  className={clsx(
                    'min-h-[44px] rounded-md border px-3 py-2 text-sm',
                    lieferwunsch === w
                      ? 'border-akzent bg-akzent-sanft text-akzent-stark'
                      : 'border-line bg-cream text-ink'
                  )}
                >
                  {LIEFERWUNSCH_LABEL[w]}
                </button>
              ))}
            </div>
            <p className="mt-1 text-xs text-ink-faint">
              „Erst wenn alles da ist" gilt, wenn niemand etwas sagt. Der Wunsch
              verhindert nichts — er meldet sich, wenn eine Sendung dagegen liefe.
            </p>
          </fieldset>

          <label className="flex min-h-[44px] items-center gap-2">
            <input
              type="checkbox"
              checked={nachrichtWunsch}
              onChange={onNachrichtWunsch}
              className="h-5 w-5"
            />
            <span className="flex items-center gap-1.5 text-sm text-ink">
              <Bell aria-hidden className="h-4 w-4 shrink-0 text-ink-soft" />
              Nachricht gewünscht, sobald alles da ist
            </span>
          </label>
          <p className="text-xs text-ink-faint">
            Wir merken uns den Wunsch und erinnern daran. Angerufen wird von Hand —
            das System verschickt nichts. Für einen einzelnen Titel steht der Wunsch
            an der Position.
          </p>
        </div>
      )}
    </section>
  );
}
